import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Phone, CheckCircle, XCircle, AlertTriangle, User, UserCheck,
  FileText, ArrowLeft, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Professional Reference Questionnaire
const PROFESSIONAL_QUESTIONS = [
  { id: 'know_candidate', question: 'Do you know the candidate?', type: 'yes_no' },
  { id: 'relationship_duration', question: 'How long have you known the candidate?', type: 'text' },
  { id: 'work_relationship', question: 'What was your working relationship with the candidate?', type: 'text' },
  { id: 'job_title', question: 'What was the candidate\'s job title/designation?', type: 'text' },
  { id: 'employment_period', question: 'What was the period of employment?', type: 'text' },
  { id: 'reporting_to', question: 'Who did the candidate report to?', type: 'text' },
  { id: 'reason_for_leaving', question: 'What was the reason for leaving?', type: 'text' },
  { id: 'eligible_for_rehire', question: 'Is the candidate eligible for rehire?', type: 'yes_no' },
  { id: 'work_quality', question: 'How would you rate the quality of work? (1-5)', type: 'rating' },
  { id: 'attendance', question: 'How was the candidate\'s attendance? (1-5)', type: 'rating' },
  { id: 'teamwork', question: 'How was the candidate\'s teamwork? (1-5)', type: 'rating' },
  { id: 'integrity', question: 'How would you rate the candidate\'s integrity? (1-5)', type: 'rating' },
  { id: 'strengths', question: 'What are the candidate\'s key strengths?', type: 'text' },
  { id: 'areas_of_improvement', question: 'What areas need improvement?', type: 'text' },
  { id: 'any_disciplinary', question: 'Was the candidate subject to any disciplinary action?', type: 'yes_no' },
  { id: 'additional_comments', question: 'Any additional comments?', type: 'text' }
];

// Personal Reference Questionnaire  
const PERSONAL_QUESTIONS = [
  { id: 'know_candidate', question: 'Do you know the candidate?', type: 'yes_no' },
  { id: 'relationship', question: 'What is your relationship with the candidate?', type: 'text' },
  { id: 'relationship_duration', question: 'How long have you known the candidate?', type: 'text' },
  { id: 'character', question: 'How would you describe the candidate\'s character?', type: 'text' },
  { id: 'trustworthy', question: 'Would you consider the candidate trustworthy? (1-5)', type: 'rating' },
  { id: 'social_behavior', question: 'How is the candidate\'s social behavior?', type: 'text' },
  { id: 'any_bad_habits', question: 'Are you aware of any bad habits?', type: 'yes_no' },
  { id: 'bad_habits_details', question: 'If yes, please specify', type: 'text' },
  { id: 'financial_stability', question: 'How would you rate the candidate\'s financial stability? (1-5)', type: 'rating' },
  { id: 'family_background', question: 'Brief about family background', type: 'text' },
  { id: 'recommend', question: 'Would you recommend this candidate?', type: 'yes_no' },
  { id: 'additional_comments', question: 'Any additional comments?', type: 'text' }
];

export default function ReferenceCheckForm({ 
  caseId, 
  checkId, 
  checkData, 
  employeeData,
  checkType = 'professional_reference', // 'professional_reference' or 'personal_reference'
  subcheckIndex = 0, // Which reference (0, 1, 2...) this form is for
  onBack,
  onComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [showRecordResponse, setShowRecordResponse] = useState(false);
  const [verificationData, setVerificationData] = useState(checkData?.reference_verification || {});
  
  const isProfessional = checkType === 'professional_reference';
  const questions = isProfessional ? PROFESSIONAL_QUESTIONS : PERSONAL_QUESTIONS;
  
  // Reference details
  const [referenceDetails, setReferenceDetails] = useState({
    reference_name: '',
    reference_designation: '',
    reference_organization: '',
    reference_contact: '',
    reference_email: '',
    relationship: ''
  });
  
  // Response form state
  const [responseForm, setResponseForm] = useState({
    agent_name: '',
    call_date: '',
    call_time: '',
    call_duration: '',
    reference_available: 'yes',
    responses: questions.map(q => ({
      question_id: q.id,
      question: q.question,
      type: q.type,
      response: '',
      remarks: ''
    })),
    overall_status: 'verified',
    overall_remarks: '',
    agent_remarks: ''
  });

  useEffect(() => {
    // Pre-populate from employee data using the correct reference index
    // First check if subcheck_data has the reference details directly
    if (checkData?.subcheck_data) {
      const ref = checkData.subcheck_data;
      setReferenceDetails({
        reference_name: ref.name || '',
        reference_designation: ref.designation || '',
        reference_organization: ref.organization || ref.company || '',
        reference_contact: ref.contact || ref.phone || '',
        reference_email: ref.email || '',
        relationship: ref.relationship || ref.relation || ''
      });
    } else if (employeeData) {
      // Fallback to employee data
      const refs = isProfessional 
        ? employeeData.professional_references || []
        : employeeData.personal_references || [];
      
      // Use the correct reference based on subcheckIndex
      const refIndex = subcheckIndex || 0;
      if (refs.length > refIndex) {
        const ref = refs[refIndex];
        setReferenceDetails({
          reference_name: ref.name || '',
          reference_designation: ref.designation || '',
          reference_organization: ref.organization || ref.company || '',
          reference_contact: ref.contact || ref.phone || '',
          reference_email: ref.email || '',
          relationship: ref.relationship || ref.relation || ''
        });
      }
    }
  }, [employeeData, isProfessional, checkData, subcheckIndex]);

  const handleRecordResponse = async () => {
    if (!responseForm.agent_name || !responseForm.call_date) {
      toast.error('Please fill agent name and call date');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.put(`/cases/${caseId}/checks/${checkId}/record-reference-verification`, {
        ...responseForm,
        reference_details: referenceDetails,
        check_type: checkType
      });
      toast.success(`Reference check recorded - Status: ${responseForm.overall_status.toUpperCase()}`);
      setVerificationData(prev => ({ ...prev, ...res.data }));
      setShowRecordResponse(false);
      if (onComplete) onComplete(res.data);
    } catch (error) {
      toast.error('Failed to record');
    } finally {
      setLoading(false);
    }
  };

  const updateResponse = (index, field, value) => {
    setResponseForm(prev => {
      const newResponses = [...prev.responses];
      newResponses[index] = { ...newResponses[index], [field]: value };
      return { ...prev, responses: newResponses };
    });
  };

  const getResultBadge = (result) => {
    switch(result) {
      case 'verified': return <Badge className="bg-emerald-100 text-emerald-700">VERIFIED</Badge>;
      case 'not_verified': return <Badge className="bg-amber-100 text-amber-700">NOT VERIFIED</Badge>;
      case 'discrepancy': return <Badge className="bg-rose-100 text-rose-700">DISCREPANCY</Badge>;
      default: return <Badge variant="outline">Pending</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <UserCheck className={`w-6 h-6 ${isProfessional ? 'text-cyan-600' : 'text-pink-600'}`} />
              {isProfessional ? 'Professional' : 'Personal'} Reference Check
            </h2>
            <p className="text-slate-600">Case ID: {caseId} | Check ID: {checkId}</p>
          </div>
        </div>
        {getResultBadge(verificationData?.overall_status)}
      </div>

      {/* Candidate Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Candidate Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-500">Name</Label>
              <p className="font-medium">{employeeData?.name || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Contact</Label>
              <p className="font-medium">{employeeData?.phone || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Email</Label>
              <p className="font-medium">{employeeData?.email || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Reference Type</Label>
              <Badge className={isProfessional ? 'bg-cyan-100 text-cyan-700' : 'bg-pink-100 text-pink-700'}>
                {isProfessional ? 'Professional' : 'Personal'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reference Details */}
      <Card className={isProfessional ? 'border-cyan-200' : 'border-pink-200'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className={isProfessional ? 'text-cyan-600' : 'text-pink-600'} />
            Reference Contact Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label>Reference Name *</Label>
              <Input 
                value={referenceDetails.reference_name}
                onChange={(e) => setReferenceDetails(prev => ({ ...prev, reference_name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            <div>
              <Label>Contact Number *</Label>
              <Input 
                value={referenceDetails.reference_contact}
                onChange={(e) => setReferenceDetails(prev => ({ ...prev, reference_contact: e.target.value }))}
                placeholder="+91-XXXXXXXXXX"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={referenceDetails.reference_email}
                onChange={(e) => setReferenceDetails(prev => ({ ...prev, reference_email: e.target.value }))}
              />
            </div>
            {isProfessional && (
              <>
                <div>
                  <Label>Designation</Label>
                  <Input 
                    value={referenceDetails.reference_designation}
                    onChange={(e) => setReferenceDetails(prev => ({ ...prev, reference_designation: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Organization</Label>
                  <Input 
                    value={referenceDetails.reference_organization}
                    onChange={(e) => setReferenceDetails(prev => ({ ...prev, reference_organization: e.target.value }))}
                  />
                </div>
              </>
            )}
            <div>
              <Label>Relationship</Label>
              <Input 
                value={referenceDetails.relationship}
                onChange={(e) => setReferenceDetails(prev => ({ ...prev, relationship: e.target.value }))}
                placeholder={isProfessional ? 'e.g., Manager, Colleague' : 'e.g., Friend, Neighbor'}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questionnaire Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {isProfessional ? 'Professional' : 'Personal'} Reference Questionnaire
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {questions.map((q, idx) => (
              <div key={idx} className="p-2 bg-slate-50 rounded text-sm">
                <span className="font-medium">{idx + 1}.</span> {q.question}
                <Badge variant="outline" className="ml-2 text-xs">
                  {q.type === 'yes_no' ? 'Yes/No' : q.type === 'rating' ? 'Rating 1-5' : 'Text'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <Button onClick={() => setShowRecordResponse(true)} className={isProfessional ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-pink-600 hover:bg-pink-700'}>
          <Phone className="w-4 h-4 mr-2" />
          Record Tele-call Response
        </Button>
      </div>

      {/* Verification Result */}
      {verificationData?.overall_status && verificationData.overall_status !== 'pending' && (
        <Card className={
          verificationData.overall_status === 'verified' ? 'border-emerald-200 bg-emerald-50' :
          verificationData.overall_status === 'discrepancy' ? 'border-rose-200 bg-rose-50' :
          'border-amber-200 bg-amber-50'
        }>
          <CardHeader>
            <CardTitle>Reference Check Result</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-slate-500">Agent Name</Label>
                <p className="font-medium">{verificationData.agent_name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Call Date</Label>
                <p className="font-medium">{verificationData.call_date || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Call Duration</Label>
                <p className="font-medium">{verificationData.call_duration || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Status</Label>
                {getResultBadge(verificationData.overall_status)}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Record Response Dialog */}
      <Dialog open={showRecordResponse} onOpenChange={setShowRecordResponse}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Record {isProfessional ? 'Professional' : 'Personal'} Reference Tele-call
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Call Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>Agent Name *</Label>
                <Input 
                  value={responseForm.agent_name}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, agent_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Call Date *</Label>
                <Input 
                  type="date"
                  value={responseForm.call_date}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, call_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Call Time</Label>
                <Input 
                  type="time"
                  value={responseForm.call_time}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, call_time: e.target.value }))}
                />
              </div>
              <div>
                <Label>Call Duration</Label>
                <Input 
                  value={responseForm.call_duration}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, call_duration: e.target.value }))}
                  placeholder="e.g., 15 mins"
                />
              </div>
            </div>

            {/* Reference Availability */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <Label className="font-medium">Reference Available for Call?</Label>
              <RadioGroup 
                value={responseForm.reference_available}
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, reference_available: v }))}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="ref-yes" />
                  <Label htmlFor="ref-yes" className="cursor-pointer">Yes - Call Completed</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="ref-no" />
                  <Label htmlFor="ref-no" className="cursor-pointer">No - Not Available</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="wrong_number" id="ref-wrong" />
                  <Label htmlFor="ref-wrong" className="cursor-pointer">Wrong Number</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Questionnaire Responses */}
            {responseForm.reference_available === 'yes' && (
              <div className="space-y-4">
                <h3 className="font-medium text-lg">Questionnaire Responses</h3>
                {responseForm.responses.map((resp, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-lg border">
                    <Label className="font-medium">{idx + 1}. {resp.question}</Label>
                    {resp.type === 'yes_no' ? (
                      <RadioGroup 
                        value={resp.response}
                        onValueChange={(v) => updateResponse(idx, 'response', v)}
                        className="flex gap-4 mt-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id={`q-${idx}-yes`} />
                          <Label htmlFor={`q-${idx}-yes`} className="cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id={`q-${idx}-no`} />
                          <Label htmlFor={`q-${idx}-no`} className="cursor-pointer">No</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="na" id={`q-${idx}-na`} />
                          <Label htmlFor={`q-${idx}-na`} className="cursor-pointer">N/A</Label>
                        </div>
                      </RadioGroup>
                    ) : resp.type === 'rating' ? (
                      <Select 
                        value={resp.response}
                        onValueChange={(v) => updateResponse(idx, 'response', v)}
                      >
                        <SelectTrigger className="w-[150px] mt-2">
                          <SelectValue placeholder="Select rating" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 - Poor</SelectItem>
                          <SelectItem value="2">2 - Below Average</SelectItem>
                          <SelectItem value="3">3 - Average</SelectItem>
                          <SelectItem value="4">4 - Good</SelectItem>
                          <SelectItem value="5">5 - Excellent</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Textarea 
                        value={resp.response}
                        onChange={(e) => updateResponse(idx, 'response', e.target.value)}
                        className="mt-2"
                        rows={2}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Overall Status */}
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <Label className="font-medium">Overall Verification Status</Label>
              <RadioGroup 
                value={responseForm.overall_status}
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, overall_status: v }))}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="verified" id="status-verified" />
                  <Label htmlFor="status-verified" className="text-emerald-700 cursor-pointer">Verified</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="not_verified" id="status-not" />
                  <Label htmlFor="status-not" className="text-amber-700 cursor-pointer">Not Verified</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="discrepancy" id="status-disc" />
                  <Label htmlFor="status-disc" className="text-rose-700 cursor-pointer">Discrepancy</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Agent Remarks */}
            <div>
              <Label>Agent Remarks</Label>
              <Textarea 
                value={responseForm.agent_remarks}
                onChange={(e) => setResponseForm(prev => ({ ...prev, agent_remarks: e.target.value }))}
                placeholder="Additional observations from the call..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordResponse(false)}>Cancel</Button>
            <Button onClick={handleRecordResponse} disabled={loading} className={isProfessional ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-pink-600 hover:bg-pink-700'}>
              <CheckCircle className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Response'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
